import { BfiApi, IApiResponse } from './bfiApi';
import FileDownload from 'js-file-download';
import { ILoanSearchCriteria, ILoanSummary } from './loanApi';
import { InsuranceOverrideType } from '../../../constants/src';

export interface IInsuranceHighlight {
    pageType: number;
    highLight: number;
}

export interface IInsuranceGridData {
    id: number;
    loanTypeId: number;
    borrower: string;
    city: string;
    state: string;
    bfiNumber: string;
    loanNumber: string;
    commitment: number;
    inFloodArea: boolean;
    floodUrl: string;
    insuranceAllGood: boolean;
    amount_GLI?: number;
    amount_GLA?: number;
    amount_BR?: number;
    amount_DW?: number;
    amount_FL?: number;
    amount_WC?: number;
    amount_OT?: number;
    expirationDate_GL?: Date;
    expirationDate_BR?: Date;
    expirationDate_DW?: Date;
    expirationDate_FL?: Date;
    expirationDate_WC?: Date;
    expirationDate_OT?: Date;
    required_GL?: boolean;
    required_BR?: boolean;
    required_DW?: boolean;
    required_FL?: boolean;
    required_WC?: boolean;
    required_OT?: boolean;
    isMissingOrExpired_GL: boolean;
    isMissingOrExpired_BR: boolean;
    isMissingOrExpired_DW: boolean;
    isMissingOrExpired_FL: boolean;
    isMissingOrExpired_WC: boolean;
    isMissingOrExpired_OT: boolean;
    bfiNamed_GL: boolean;
    bfiNamed_BR: boolean;
    bfiNamed_DW: boolean;
    bfiNamed_FL: boolean;
    bfiNamed_WC: boolean;
    bfiNamed_OT: boolean;
    terminationTypeId_GL: number;
    terminationTypeId_BR: number;
    terminationTypeId_DW: number;
    terminationTypeId_FL: number;
    terminationTypeId_WC: number;
    terminationTypeId_OT: number;
    cancelled_GL: boolean;
    cancelled_BR: boolean;
    cancelled_DW: boolean;
    cancelled_FL: boolean;
    cancelled_WC: boolean;
    cancelled_OT: boolean;
    underwriter: string;
    owner: string;
    broker: string;
    canDelete: boolean;
    subNoteCount: number;
}

export interface IInsuranceFile {
    id: number;
    fileTypeId: number;
    fileTypeName: string;
    url: string;
    tempFileName: string;
    uploadDate: Date;
}

export interface IInsurance {
    id: number;
    insuranceTypeId: number;
    name: string;
    amount1?: number;
    amount2?: number;
    expirationDate?: Date;
    bfiNamed: boolean;
    areChecksOverriden: boolean;
    OverrideTypeId?: InsuranceOverrideType;
    files: [IInsuranceFile];
    forcePlaced?: boolean;
    forcePlacedDate?: Date;
}

export interface IInsuranceGroup {
    allGood: boolean;
    notes: string;
    insurances: [IInsurance];
}

export interface IPropertyInsurances extends ILoanSummary {
    insuranceGroup: IInsuranceGroup;
}

export interface IInsuranceHighlightsResponse extends Omit<IApiResponse, 'result'> {
    result: IInsuranceHighlight[];
}

export interface IInsuranceGridResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [IInsuranceGridData];
    };
}

export interface IPropertyInsurancesResponse extends Omit<IApiResponse, 'result'> {
    result: IPropertyInsurances;
}

export class InsuranceApi extends BfiApi {
    exportInsurance(loanTypeId) {
        this.axiosInstance({
            url: `/loans/insurance/export/${loanTypeId}`,
            method: 'GET',
            responseType: 'blob', // Important
        }).then((response) => {
            FileDownload(response.data, 'export.csv');
        });
    }

    getHighlights(): Promise<IInsuranceHighlightsResponse> {
        return super.get('/loans/insurance/highlights');
    }

    getNotClosedMissing(criteria: ILoanSearchCriteria): Promise<IInsuranceGridResponse> {
        return super.post('/loans/insurance/grids/notclosed/missing', criteria);
    }

    getNotClosedGood(criteria: ILoanSearchCriteria): Promise<IInsuranceGridResponse> {
        return super.post('/loans/insurance/grids/notclosed/good', criteria);
    }

    getOutstandingExpired(criteria: ILoanSearchCriteria): Promise<IInsuranceGridResponse> {
        return super.post('/loans/insurance/grids/outstanding/missing', criteria);
    }

    getOutstandingGood(criteria: ILoanSearchCriteria): Promise<IInsuranceGridResponse> {
        return super.post('/loans/insurance/grids/outstanding/good', criteria);
    }

    getRepaid(criteria: ILoanSearchCriteria): Promise<IInsuranceGridResponse> {
        return super.post('/loans/insurance/grids/repaid', criteria);
    }

    getPropertyInsurances(loanId: number, propertyId: number): Promise<IPropertyInsurancesResponse> {
        return super.get(`loans/${loanId}/properties/${propertyId}/insurance`);
    }

    setPropertyInsurances(loanId: number, propertyId: number, model: IInsuranceGroup): Promise<IApiResponse> {
        return super.post(`/loans/${loanId}/properties/${propertyId}/insurance`, model);
    }
}
